import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShieldCheckIcon, 
  EyeIcon, 
  LockClosedIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = 'December 15, 2024';

  const sections = [
    {
      title: 'Information We Collect',
      icon: EyeIcon,
      content: [
        'Personal information (name, email, profile data)',
        'Usage data and analytics',
        'Team collaboration data',
        'Device and browser information',
        'Cookies and similar technologies'
      ]
    },
    {
      title: 'How We Use Your Information',
      icon: UserIcon,
      content: [
        'Provide and maintain our services',
        'Process transactions and manage accounts',
        'Send important updates and notifications',
        'Improve our products and user experience',
        'Ensure security and prevent fraud'
      ]
    },
    {
      title: 'Data Security',
      icon: LockClosedIcon,
      content: [
        'Encryption of data in transit and at rest',
        'Regular security audits and assessments',
        'Access controls and authentication',
        'Secure data centers and infrastructure',
        'Employee training on data protection'
      ]
    },
    {
      title: 'Data Sharing',
      icon: DocumentTextIcon,
      content: [
        'We do not sell your personal information',
        'Limited sharing with service providers',
        'Legal compliance when required',
        'With your explicit consent',
        'Anonymized data for analytics'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
              <ShieldCheckIcon className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-xl text-gray-600 mb-4">
            Your privacy is important to us. This policy explains how we collect, use, and protect your information.
          </p>
          <div className="flex items-center justify-center text-sm text-gray-500">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Last updated: {lastUpdated}
          </div>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
          <p className="text-gray-600 leading-relaxed mb-4">
            At StyleSync, we are committed to protecting your privacy and ensuring the security of your personal information. 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
            collaborative design platform.
          </p>
          <p className="text-gray-600 leading-relaxed">
            By using StyleSync, you agree to the collection and use of information in accordance with this policy. 
            If you have any questions about this Privacy Policy, please contact us at privacy@stylesync.com.
          </p>
        </motion.div>

        {/* Policy Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mr-4">
                  <section.icon className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-gray-900">{section.title}</h2>
              </div>
              <ul className="space-y-3">
                {section.content.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Additional Sections */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mt-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-6">Your Rights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Access and Control</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Access your personal data</li>
                <li>• Update or correct your information</li>
                <li>• Request deletion of your data</li>
                <li>• Export your data</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Communication Preferences</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Opt-out of marketing emails</li>
                <li>• Control notification settings</li>
                <li>• Manage team invitations</li>
                <li>• Set privacy preferences</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 p-8 mt-8"
        >
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
          <p className="text-gray-600 mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-gray-600">
            <p><strong>Email:</strong> privacy@stylesync.com</p>
            <p><strong>Address:</strong> StyleSync Inc., Toronto, Ontario, Canada</p>
            <p><strong>Phone:</strong> +1 (555) 123-4567</p>
          </div>
        </motion.div>

        {/* Updates Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mt-8"
        >
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">Policy Updates</h3>
          <p className="text-yellow-700">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
            the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to 
            review this Privacy Policy periodically for any changes.
          </p>
        </motion.div>
      </main>

      <AppFooter />
    </div>
  );
};

export default PrivacyPolicyPage; 